import { useCallback, useRef, useState } from "react";
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldErrors,
  useFieldArray,
} from "react-hook-form";
import {
  Map,
  Building2,
  Factory,
  Anchor,
  MapPin,
  AlertCircle,
  Plus,
} from "lucide-react";
import { FormField, Input } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import {
  MAX_ADDITIONAL,
  OrderAdditionalStops,
  makeAdditionalStop,
} from "./OrderAdditionalStops";
import { DirectorySelect } from "../directory/DirectorySelect";
import { AddressAutocompleteField } from "../GooglePlaces/AddressAutocompleteField";
import type { PlacesApiStatus } from "../GooglePlaces/GooglePlacesAutoComplete";
import type { Country, EntitySnapshot } from "../../types";
import type { ContainerOrderFormData } from "../../types/containerOrders";
import { useTranslation } from "react-i18next";

export type StopBase =
  | "route.terminalPickup"
  | "route.loadingSite"
  | "route.terminalDropoff";

/** DOM handles on the three address inputs, owned by the parent form so a
 *  blocked save can focus the stop that blocked it. */
export type StopAddressRefs = Record<
  StopBase,
  React.MutableRefObject<HTMLInputElement | null>
>;

type Coordinates = { latitude: number; longitude: number };

interface RouteTerminalsSectionProps {
  /** Needed for the added stops, which are a field array rather than fixed paths. */
  control: Control<ContainerOrderFormData>;
  register: UseFormRegister<ContainerOrderFormData>;
  setValue: UseFormSetValue<ContainerOrderFormData>;
  watch: UseFormWatch<ContainerOrderFormData>;
  errors: FieldErrors<ContainerOrderFormData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
  countries: Country[];
  apiKey: string;
  /**
   * Whether a save has been attempted. The location hints stay advisory until
   * the user has actually been stopped by one — see `locationHint`.
   */
  isSubmitted: boolean;
  addressRefs: StopAddressRefs;
}

/** Per-stop address UI state — none of it belongs in the form values. */
interface StopUiState {
  /** The address field has been left without a suggestion being picked. */
  blurred: boolean;
  /**
   * The address text has been typed into since it was last set from a pick or
   * loaded from the order. Distinguishes "the user is mid-word" — where a
   * warning would be premature — from "this stored address has no location",
   * which is worth saying straight away.
   */
  edited: boolean;
  /** A suggestion was picked but Places returned no geometry for it. */
  noGeometry: boolean;
  /** A suggestion was picked but its details lookup failed. */
  lookupFailed: boolean;
  /** A directory record was selected that has no stored map location. */
  directoryHasNoLocation: boolean;
}

/** The date and time inputs of a stop's own slot. */
type StopSlotPath =
  | "route.terminalPickup.pickupDate"
  | "route.terminalPickup.pickupTime"
  | "route.loadingSite.onSiteDate"
  | "route.loadingSite.onSiteTime"
  | "route.terminalDropoff.deliveryDate"
  | "route.terminalDropoff.deliveryTime";

/**
 * Each stop's reference is stored under the same key (`bookingRef`) but is
 * labelled for the stop it belongs to — a driver reads "delivery ref" off the
 * drop-off card, not a second "booking ref".
 */
const REF_LABEL_KEY: Record<StopBase, string> = {
  "route.terminalPickup": "route.pickupRef",
  "route.loadingSite": "route.loadingSiteRef",
  "route.terminalDropoff": "route.deliveryRef",
};

const emptyStopUi: StopUiState = {
  blurred: false,
  edited: false,
  noGeometry: false,
  lookupFailed: false,
  directoryHasNoLocation: false,
};

/**
 * Section 3 — route & terminals, shared across all shipments.
 *
 * Three stops, in running order. Each card leads with its reference and slot —
 * ref, date, time — and follows with where it is:
 *   1. Terminal pickup   — Ref, Pickup date/time, Terminal, Address, PIN
 *   2. Loading site      — Ref, On-site date/time, Company, Address, Ramp,
 *                          Contact, PIN
 *   3. Terminal drop-off — Ref, Delivery date/time, Terminal, Address, PIN,
 *                          Cut-off date, Cut-off time
 *
 * Each stop's slot is named for what happens there — collection, the visit to
 * site, the return — rather than all three being called "date", so a card can be
 * read on its own. Only the terminal and its address are required.
 *
 * Address is one row. Picking a record from the Addresses directory (or a Google
 * Places suggestion) fills the structured city / state / postcode / country
 * underneath, so generated shipments still get a complete address without the
 * form showing five separate boxes.
 *
 * All three stops are picked from the Addresses directory — the loading site
 * included. It is a place a truck is sent to, not a billing identity; the order's
 * client is the partner, and that is set in the general info section above.
 *
 * All three names must be directory records — they are picked, never typed.
 * Every shipment in the batch is dispatched to these stops, so a hand-typed
 * terminal would send a truck to a place no record backs. See DirectorySelect.
 */
export function RouteTerminalsSection({
  control,
  register,
  setValue,
  watch,
  errors,
  readonly,
  getErrorMessage,
  countries,
  apiKey,
  isSubmitted,
  addressRefs,
}: RouteTerminalsSectionProps) {
  const { t } = useTranslation(["containerOrders", "shipments"]);

  /**
   * The added stops, held here rather than inside the card list they render
   * into: "Add stop" belongs in the section header, and the header cannot reach
   * a field array declared below it.
   */
  const { fields: stopFields, append: appendStop, remove: removeStop } =
    useFieldArray({ control, name: "route.additionalStops" });

  const pickupName = watch("route.terminalPickup.companyName");
  const pickupStreet = watch("route.terminalPickup.address.street");
  const pickupAddressId = watch("route.terminalPickup.addressId");
  const loadingName = watch("route.loadingSite.companyName");
  const loadingStreet = watch("route.loadingSite.address.street");
  const loadingAddressId = watch("route.loadingSite.addressId");
  const dropoffName = watch("route.terminalDropoff.companyName");
  const dropoffStreet = watch("route.terminalDropoff.address.street");
  const dropoffAddressId = watch("route.terminalDropoff.addressId");

  /**
   * Whether each terminal is part of this job at all.
   *
   * `!== false` rather than a truth test, because the field is absent on orders
   * written before a stop could be turned off — and those all collect and return
   * the box, which is the only thing the schema could express at the time.
   */
  const pickupUsed = watch("route.terminalPickup.enabled") !== false;
  const dropoffUsed = watch("route.terminalDropoff.enabled") !== false;

  /**
   * Map location per stop.
   *
   * A stop is a place a truck is sent to, so — exactly as in the Addresses and
   * Partners directories — its address has to be pinned on the map. There are
   * two legitimate sources: a Google Places pick, or a directory record that
   * already carries one. Anything typed by hand has no location and cannot be
   * saved, and the API enforces the same rule.
   */
  const coordinatesByStop: Record<StopBase, Coordinates | undefined> = {
    "route.terminalPickup": watch(
      "route.terminalPickup.address.coordinates",
    ) as Coordinates | undefined,
    "route.loadingSite": watch("route.loadingSite.address.coordinates") as
      | Coordinates
      | undefined,
    "route.terminalDropoff": watch(
      "route.terminalDropoff.address.coordinates",
    ) as Coordinates | undefined,
  };

  const hasCoordinates = (base: StopBase) => {
    const value = coordinatesByStop[base];
    return (
      typeof value?.latitude === "number" && typeof value?.longitude === "number"
    );
  };

  const [placesStatus, setPlacesStatus] = useState<PlacesApiStatus>("loading");
  const handlePlacesStatusChange = useCallback(
    (status: PlacesApiStatus) => setPlacesStatus(status),
    [],
  );

  const [stopUi, setStopUi] = useState<Record<StopBase, StopUiState>>({
    "route.terminalPickup": emptyStopUi,
    "route.loadingSite": emptyStopUi,
    "route.terminalDropoff": emptyStopUi,
  });

  const patchStopUi = useCallback(
    (base: StopBase, patch: Partial<StopUiState>) =>
      setStopUi((prev) => ({ ...prev, [base]: { ...prev[base], ...patch } })),
    [],
  );

  /**
   * The last address picked per stop, so retyping (or undoing an edit back to)
   * that exact text restores its coordinates instead of silently losing them.
   */
  const lastPickRef = useRef<
    Record<StopBase, { text: string; coordinates?: Coordinates } | null>
  >({
    "route.terminalPickup": null,
    "route.loadingSite": null,
    "route.terminalDropoff": null,
  });

  const setCoordinates = useCallback(
    (base: StopBase, coordinates?: Coordinates) =>
      setValue(`${base}.address.coordinates` as any, coordinates, {
        shouldValidate: true,
      }),
    [setValue],
  );

  /**
   * Fill a stop from an Addresses snapshot.
   *
   * One directory for all three stops, because a pickup terminal, a client's
   * loading site and a drop-off terminal are the same kind of thing: a place a
   * truck is sent to, with a street, a gate and a map location.
   */
  const applySnapshot = useCallback(
    (base: StopBase, idField: "addressId", snapshot: EntitySnapshot) => {
      const country = snapshot?.address?.country;
      // The directory link and the name it came with are both required and
      // neither has a registered input, so they are validated on assignment —
      // otherwise a stale "pick a terminal" would sit under a filled stop.
      setValue(`${base}.${idField}` as any, snapshot._id ?? null, {
        shouldValidate: true,
      });
      setValue(`${base}.companyName` as any, snapshot.companyName, {
        shouldValidate: true,
      });
      // `shouldValidate` on the two required parts: neither has a registered
      // input that would re-validate on its own, so a stale "address is
      // required" would otherwise sit under a stop that has just been filled.
      setValue(`${base}.address.street` as any, snapshot.address?.street || "", {
        shouldValidate: true,
      });
      setValue(`${base}.address.city` as any, snapshot.address?.city || "", {
        shouldValidate: true,
      });
      setValue(`${base}.address.state` as any, snapshot.address?.state || "");
      setValue(
        `${base}.address.postalCode` as any,
        snapshot.address?.postalCode || "",
      );
      setValue(
        `${base}.address.country` as any,
        typeof country === "object" && country !== null ? country._id : country,
      );

      // The directory is the other legitimate source of a map location, so a
      // terminal or partner picked here needs no second trip through Places.
      // Records saved before coordinates existed have none — the stop then
      // says so and asks for the address to be picked from the suggestions.
      const coordinates = snapshot.address?.coordinates ?? undefined;
      const located =
        typeof coordinates?.latitude === "number" &&
        typeof coordinates?.longitude === "number";
      setCoordinates(base, located ? coordinates! : undefined);
      lastPickRef.current[base] = {
        text: snapshot.address?.street || "",
        coordinates: located ? coordinates! : undefined,
      };
      patchStopUi(base, {
        ...emptyStopUi,
        directoryHasNoLocation: !located && !!snapshot.address?.street,
      });
    },
    [setValue, setCoordinates, patchStopUi],
  );

  const clearStop = useCallback(
    (base: StopBase, idField: "addressId") => {
      setValue(`${base}.${idField}` as any, null);
      setValue(`${base}.companyName` as any, "");
      setValue(`${base}.address.street` as any, "");
      setValue(`${base}.address.city` as any, "");
      setValue(`${base}.address.state` as any, "");
      setValue(`${base}.address.postalCode` as any, "");
      setValue(`${base}.address.country` as any, "");
      setCoordinates(base, undefined);
      lastPickRef.current[base] = null;
      patchStopUi(base, emptyStopUi);
    },
    [setValue, setCoordinates, patchStopUi],
  );

  const applyPlace = useCallback(
    (base: StopBase, address: any) => {
      const coordinates: Coordinates | undefined =
        address.latitude != null && address.longitude != null
          ? { latitude: address.latitude, longitude: address.longitude }
          : undefined;

      setValue(`${base}.address.street` as any, address.street, {
        shouldValidate: true,
      });
      // Prefilled from the pick but still hand-editable — a field Places had
      // nothing for keeps whatever is already there rather than being blanked.
      if (address.city)
        setValue(`${base}.address.city` as any, address.city, {
          shouldValidate: true,
        });
      if (address.state) setValue(`${base}.address.state` as any, address.state);
      if (address.postalCode)
        setValue(`${base}.address.postalCode` as any, address.postalCode);
      if (address.country)
        setValue(`${base}.address.country` as any, address.country);

      setCoordinates(base, coordinates);
      lastPickRef.current[base] = { text: address.street ?? "", coordinates };
      patchStopUi(base, { ...emptyStopUi, noGeometry: !coordinates });
    },
    [setValue, setCoordinates, patchStopUi],
  );

  /**
   * Typing in the address box means the text no longer describes the place that
   * was picked, so its coordinates are stale — drop them. Typing (or undoing)
   * back to the picked address restores them, so a stray keystroke does not
   * cost another round trip through the dropdown.
   */
  const handleAddressTyped = useCallback(
    (base: StopBase, value: string) => {
      setValue(`${base}.address.street` as any, value, {
        shouldValidate: isSubmitted,
      });

      const pick = lastPickRef.current[base];
      const backAtPick = !!pick && pick.text.trim() === value.trim();
      setCoordinates(base, backAtPick ? pick!.coordinates : undefined);
      patchStopUi(base, {
        ...emptyStopUi,
        edited: true,
        noGeometry: backAtPick ? !pick!.coordinates : false,
      });
    },
    [setValue, setCoordinates, patchStopUi, isSubmitted],
  );

  /**
   * Which hint to show under an unlocated address, and how loudly. Neutral
   * while the user is still typing, amber once they have moved on without
   * picking, red only once a save has actually been blocked.
   */
  const locationHint = (base: StopBase, street?: string | null) => {
    const ui = stopUi[base];
    const placesUnavailable = !apiKey || placesStatus === "error";
    // An address that is on screen without a location is worth flagging — but
    // not while it is still being typed, where every keystroke is legitimately
    // "not picked yet".
    const settledWithoutLocation = !!street && (ui.blurred || !ui.edited);
    const stalled =
      placesUnavailable ||
      ui.lookupFailed ||
      ui.noGeometry ||
      ui.directoryHasNoLocation ||
      settledWithoutLocation;

    const key = placesUnavailable
      ? "route.locationLookupUnavailable"
      : ui.lookupFailed
        ? "route.locationLookupFailed"
        : ui.noGeometry
          ? "route.locationUnavailableForPlace"
          : ui.directoryHasNoLocation
            ? "route.locationMissingOnRecord"
            : isSubmitted
              ? "route.locationRequired"
              : settledWithoutLocation
                ? "route.locationNotCaptured"
                : "route.locationSelectFromSuggestions";

    return {
      key,
      tone: isSubmitted
        ? "text-red-600"
        : stalled
          ? "text-signal"
          : "text-mist",
    };
  };

  /**
   * Address row — street autocomplete plus the city, and the map-location
   * state underneath.
   *
   * City is shown rather than hidden because it is required (it becomes the
   * generated shipment's pickup/delivery city). Selecting a terminal from the
   * directory or a Google Places suggestion fills it automatically; typing a
   * street by hand does not, and an invisible required field would be an
   * error the user could not clear. City stays hand-editable — only the map
   * location is tied to the pick.
   */
  const addressRow = (
    base: StopBase,
    street: string | null | undefined,
    addressError?: string,
  ) => {
    const located = hasCoordinates(base);
    const coordinates = coordinatesByStop[base];
    const hint = locationHint(base, street);

    return (
      <FormField
        layout="inline"
        size="sm"
        label={t("route.address")}
        required
        error={addressError}
      >
        <div className="space-y-1">
          <div className="grid grid-cols-1 gap-2 @xs/control:grid-cols-3">
            <div className="@xs/control:col-span-2">
              <AddressAutocompleteField
                apiKey={apiKey}
                value={street || ""}
                onChange={(value) => handleAddressTyped(base, value)}
                onAddressSelect={(address) => applyPlace(base, address)}
                onBlur={() => patchStopUi(base, { blurred: true })}
                onStatusChange={handlePlacesStatusChange}
                onPlaceSelectError={() =>
                  patchStopUi(base, { lookupFailed: true })
                }
                inputRef={addressRefs[base]}
                countries={countries}
                disabled={readonly}
                error={!!addressError || (isSubmitted && !located)}
                size="sm"
              />
            </div>
            <Input
              {...register(`${base}.address.city` as any)}
              size="sm"
              readOnly={readonly}
              placeholder={t("placeholders.city")}
              error={!!addressError}
            />
          </div>

          {/* Say where the location stands at every step — while typing, on
              leaving the field, and on save — rather than only failing at the
              end with a message the user has to work backwards from. */}
          {!readonly &&
            (located ? (
              <p className="flex items-center gap-1 text-[11px] text-success-600">
                <MapPin className="h-3 w-3 shrink-0" />
                {t("route.locationCaptured", {
                  lat: coordinates!.latitude.toFixed(5),
                  lng: coordinates!.longitude.toFixed(5),
                })}
              </p>
            ) : (
              <p className={`flex items-center gap-1 text-[11px] ${hint.tone}`}>
                <AlertCircle className="h-3 w-3 shrink-0" />
                {t(hint.key)}
              </p>
            ))}
        </div>
      </FormField>
    );
  };

  /**
   * The error sitting at a dotted path, e.g. `route.loadingSite.onSiteDate`.
   *
   * The route-order rule is a test on the whole `route` object, so it reports
   * against whichever slot is out of order rather than against a field the form
   * can look up statically — hence the walk.
   */
  const errorAt = (path: StopSlotPath) =>
    path
      .split(".")
      .reduce<unknown>(
        (node, key) =>
          node && typeof node === "object"
            ? (node as Record<string, unknown>)[key]
            : undefined,
        errors,
      ) as { message?: string } | undefined;

  /**
   * The head of every stop card: its reference, then its slot — date, then
   * time. These lead the card rather than following the address because they
   * are what changes: which terminal a stop is at is settled once, while the
   * ref and the slot are what a dispatcher opens the order to fill in or move.
   *
   * All three are optional — a stop whose slot is not booked yet still saves —
   * but when two stops both carry a date they must run in route order; see
   * `containerOrderSchema`.
   *
   * Written as one helper rather than three copies: the three stops differ only
   * in the field names behind them, and keeping the markup in one place is what
   * stops them drifting apart visually.
   */
  const stopSlotRows = (
    base: StopBase,
    slot: {
      /** Full paths, since each stop names its slot after what happens there. */
      date: StopSlotPath;
      time: StopSlotPath;
      dateLabel: string;
      timeLabel: string;
    },
  ) => (
    <>
      {/* Every stop has its own reference — the label says which one, so the
          field itself is just "ref". */}
      <FormField layout="inline" size="sm" label={t(REF_LABEL_KEY[base])}>
        <Input
          {...register(`${base}.bookingRef`)}
          size="sm"
          readOnly={readonly}
          className="font-mono placeholder:font-sans"
          placeholder={t("placeholders.stopRef")}
        />
      </FormField>

      <FormField
        layout="inline"
        size="sm"
        label={slot.dateLabel}
        error={getErrorMessage(errorAt(slot.date))}
      >
        <Input
          {...register(slot.date)}
          type="date"
          size="sm"
          readOnly={readonly}
          error={!!errorAt(slot.date)}
        />
      </FormField>

      <FormField
        layout="inline"
        size="sm"
        label={slot.timeLabel}
        error={getErrorMessage(errorAt(slot.time))}
      >
        <Input
          {...register(slot.time)}
          type="time"
          size="sm"
          readOnly={readonly}
          error={!!errorAt(slot.time)}
        />
      </FormField>
    </>
  );

  /** The gate PIN, which sits with the address it gets you through. */
  const stopPinRow = (base: StopBase) => (
    <FormField layout="inline" size="sm" label={t("route.pin")}>
      <Input
        {...register(`${base}.pin`)}
        size="sm"
        readOnly={readonly}
        className="font-mono placeholder:font-sans"
        placeholder={t("placeholders.pin")}
      />
    </FormField>
  );

  const stopHeader = (
    num: number,
    Icon: typeof Building2,
    title: string,
    kind: string,
    accent: string,
    used = true,
  ) => (
    <div className="mb-2 flex items-center gap-2">
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${
          used ? accent : "bg-secondary-300"
        }`}
      >
        {num}
      </span>
      <Icon
        className={`h-3.5 w-3.5 shrink-0 ${used ? "text-mist" : "text-secondary-300"}`}
      />
      <span
        className={`truncate text-[12px] font-semibold ${
          used ? "text-ink" : "text-mist line-through"
        }`}
      >
        {title}
      </span>
      <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-mist">
        {kind}
      </span>
    </div>
  );

  /**
   * "No pickup" / "No delivery" — the switch that takes a stop off the route.
   *
   * A container move does not always start or end at a terminal: the box may
   * already be standing at the client's site, or be staying there when the truck
   * leaves. Neither had any way of being said, so the dispatcher's only options
   * were to invent a terminal or to leave every driver on the batch with a step
   * nobody was going to take.
   *
   * A switch rather than a checkbox because it is not a property being recorded,
   * it is the stop being turned off — and it says what the state *is* rather than
   * what clicking would do, which is what makes a row of stop cards scannable.
   *
   * The reason underneath is required, and it is the point of the whole control:
   * it travels down to every container generated from this order and onto the
   * driver's screen. "No pickup" on its own is an instruction nobody can check.
   */
  const stopUsageRow = (
    base: "route.terminalPickup" | "route.terminalDropoff",
    used: boolean,
    offLabel: string,
  ) => (
    <div className="mb-2 border-b border-[rgba(11,15,14,0.06)] pb-2">
      <button
        type="button"
        role="switch"
        aria-checked={!used}
        disabled={readonly}
        onClick={() => {
          setValue(`${base}.enabled` as any, !used, { shouldValidate: true });
          // Turning it back on takes the reason with it: an order that went on
          // carrying "box already on site" while a truck was being sent to the
          // terminal would show that sentence to every driver on the batch.
          if (!used) setValue(`${base}.skipReason` as any, "");
        }}
        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-60 ${
          used
            ? "bg-bone text-mist hover:bg-secondary-100"
            : "bg-copper text-white"
        }`}
      >
        <span
          className={`flex h-3 w-3 rounded-full transition-colors ${
            used ? "bg-secondary-300" : "bg-white"
          }`}
        />
        {used ? offLabel : t("route.notUsed")}
      </button>

      {!used && (
        <div className="mt-2">
          <FormField
            layout="inline"
            size="sm"
            required
            label={t("route.skipReason")}
            hint={t("route.skipReasonHintOrder")}
            error={getErrorMessage(
              base === "route.terminalPickup"
                ? errors.route?.terminalPickup?.skipReason
                : errors.route?.terminalDropoff?.skipReason,
            )}
          >
            <Input
              {...register(`${base}.skipReason` as any)}
              size="sm"
              readOnly={readonly}
              placeholder={t("route.skipReasonPlaceholder")}
              error={
                !!(base === "route.terminalPickup"
                  ? errors.route?.terminalPickup?.skipReason
                  : errors.route?.terminalDropoff?.skipReason)
              }
            />
          </FormField>
        </div>
      )}
    </div>
  );

  return (
    <SectionCard
      icon={Map}
      title={t("sections.routeTerminals")}
      note={<p className="text-[11px]">{t("hints.routeShared")}</p>}
      /* "Add stop" sits with the section it adds to, rather than on the
         sub-heading of the added-stops list further down the card — on a wide
         screen that heading is three cards below the fold, and the control that
         changes the shape of the route should be where the route is named.
         Hidden rather than disabled at the ceiling; the list below says why. */
      actions={
        !readonly && stopFields.length < MAX_ADDITIONAL ? (
          <button
            type="button"
            onClick={() => appendStop(makeAdditionalStop(stopFields.length))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[rgba(11,15,14,0.15)] px-2.5 py-1 text-[12px] font-medium text-ink hover:bg-bone"
          >
            <Plus className="h-3.5 w-3.5" />
            {t("route.addStop")}
          </button>
        ) : undefined
      }
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {/* ── Stop 1: terminal pickup ─────────────────────────────────── */}
        <div
          className={`@container rounded-md border p-3 ${
            pickupUsed
              ? "border-[rgba(11,15,14,0.08)]"
              : "border-dashed border-[rgba(11,15,14,0.18)] bg-bone/60"
          }`}
        >
          {stopHeader(
            1,
            Building2,
            t("route.terminalPickup"),
            t("route.kindTerminal"),
            "bg-ink",
            pickupUsed,
          )}

          {stopUsageRow(
            "route.terminalPickup",
            pickupUsed,
            t("route.markNoPickup"),
          )}

          {/* The fields are kept rather than unmounted when the stop is turned
              off — turning it back on is then a click rather than a re-entry —
              but they are not shown, because a form for a place nobody is
              driving to invites someone to fill it in. */}
          <div className={`space-y-1 ${pickupUsed ? "" : "hidden"}`}>
            {stopSlotRows("route.terminalPickup", {
              date: "route.terminalPickup.pickupDate",
              time: "route.terminalPickup.pickupTime",
              dateLabel: t("route.pickupDate"),
              timeLabel: t("route.pickupTime"),
            })}

            <FormField
              layout="inline"
              size="sm"
              label={t("route.terminal")}
              required
              // The missing-link message is the actionable one; the plain
              // "terminal is required" fires alongside it on an empty stop.
              error={
                getErrorMessage(errors.route?.terminalPickup?.addressId) ??
                getErrorMessage(errors.route?.terminalPickup?.companyName)
              }
            >
              <DirectorySelect
                // Terminals live in the Addresses directory, not Partners.
                source="addresses"
                selectedId={pickupAddressId}
                value={pickupName || ""}
                onSelect={(snapshot) =>
                  applySnapshot("route.terminalPickup", "addressId", snapshot)
                }
                onClear={() => clearStop("route.terminalPickup", "addressId")}
                disabled={readonly}
                size="sm"
                placeholder={t("placeholders.terminal")}
                error={
                  !!errors.route?.terminalPickup?.addressId ||
                  !!errors.route?.terminalPickup?.companyName
                }
              />
            </FormField>

            {addressRow(
              "route.terminalPickup",
              pickupStreet,
              getErrorMessage(errors.route?.terminalPickup?.address?.street) ??
                getErrorMessage(errors.route?.terminalPickup?.address?.city),
            )}

            {stopPinRow("route.terminalPickup")}
          </div>
        </div>

        {/* ── Stop 2: loading site ────────────────────────────────────── */}
        <div className="@container rounded-md border border-[rgba(11,15,14,0.08)] p-3">
          {stopHeader(
            2,
            Factory,
            t("route.loadingSite"),
            t("route.kindClient"),
            "bg-copper",
          )}

          <div className="space-y-1">
            {stopSlotRows("route.loadingSite", {
              date: "route.loadingSite.onSiteDate",
              time: "route.loadingSite.onSiteTime",
              dateLabel: t("route.onSiteDate"),
              timeLabel: t("route.onSiteTime"),
            })}

            <FormField
              layout="inline"
              size="sm"
              label={t("shipments:labels.company")}
              required
              error={
                getErrorMessage(errors.route?.loadingSite?.addressId) ??
                getErrorMessage(errors.route?.loadingSite?.companyName)
              }
            >
              <DirectorySelect
                source="addresses"
                selectedId={loadingAddressId}
                value={loadingName || ""}
                onSelect={(snapshot) => {
                  applySnapshot("route.loadingSite", "addressId", snapshot);
                  // Contact details come off the directory record but stay
                  // editable — a site contact often differs from head office.
                  setValue(
                    "route.loadingSite.contactPhone",
                    snapshot.phoneNumber || "",
                  );
                  setValue(
                    "route.loadingSite.contactEmail",
                    snapshot.emailAddress || "",
                  );
                }}
                onClear={() => {
                  clearStop("route.loadingSite", "addressId");
                  setValue("route.loadingSite.contactName", "");
                  setValue("route.loadingSite.contactPhone", "");
                  setValue("route.loadingSite.contactEmail", "");
                }}
                disabled={readonly}
                size="sm"
                placeholder={t("placeholders.loadingSite")}
                error={
                  !!errors.route?.loadingSite?.addressId ||
                  !!errors.route?.loadingSite?.companyName
                }
              />
            </FormField>

            {addressRow(
              "route.loadingSite",
              loadingStreet,
              getErrorMessage(errors.route?.loadingSite?.address?.street) ??
                getErrorMessage(errors.route?.loadingSite?.address?.city),
            )}

            <FormField layout="inline" size="sm" label={t("route.ramp")}>
              <Input
                {...register("route.loadingSite.ramp")}
                size="sm"
                readOnly={readonly}
                placeholder={t("placeholders.ramp")}
              />
            </FormField>

            {/* Contact — name + phone on one row, as in the mockup. */}
            <FormField
              layout="inline"
              size="sm"
              label={t("route.contact")}
              error={getErrorMessage(errors.route?.loadingSite?.contactName)}
            >
              <div className="grid grid-cols-1 gap-2 @2xs/control:grid-cols-2">
                <Input
                  {...register("route.loadingSite.contactName")}
                  size="sm"
                  readOnly={readonly}
                  placeholder={t("placeholders.contactName")}
                />
                <Input
                  {...register("route.loadingSite.contactPhone")}
                  size="sm"
                  readOnly={readonly}
                  placeholder={t("placeholders.contactPhone")}
                />
              </div>
            </FormField>

            {stopPinRow("route.loadingSite")}
          </div>
        </div>

        {/* ── Stop 3: terminal drop-off ───────────────────────────────── */}
        <div
          className={`@container rounded-md border p-3 ${
            dropoffUsed
              ? "border-[rgba(11,15,14,0.08)]"
              : "border-dashed border-[rgba(11,15,14,0.18)] bg-bone/60"
          }`}
        >
          {stopHeader(
            3,
            Anchor,
            t("route.terminalDropoff"),
            t("route.kindTerminal"),
            "bg-ink",
            dropoffUsed,
          )}

          {stopUsageRow(
            "route.terminalDropoff",
            dropoffUsed,
            t("route.markNoDelivery"),
          )}

          {/* The cut-off stays visible even with the terminal turned off: it is
              the deadline the whole run answers to, the batch list has a column
              for it, and a box that is not going back to a terminal still has a
              sailing to make. */}
          <div className={`space-y-1 ${dropoffUsed ? "" : "hidden"}`}>
            {stopSlotRows("route.terminalDropoff", {
              date: "route.terminalDropoff.deliveryDate",
              time: "route.terminalDropoff.deliveryTime",
              dateLabel: t("route.deliveryDate"),
              timeLabel: t("route.deliveryTime"),
            })}

            <FormField
              layout="inline"
              size="sm"
              label={t("route.terminal")}
              required
              error={
                getErrorMessage(errors.route?.terminalDropoff?.addressId) ??
                getErrorMessage(errors.route?.terminalDropoff?.companyName)
              }
            >
              <DirectorySelect
                source="addresses"
                selectedId={dropoffAddressId}
                value={dropoffName || ""}
                onSelect={(snapshot) =>
                  applySnapshot("route.terminalDropoff", "addressId", snapshot)
                }
                onClear={() => clearStop("route.terminalDropoff", "addressId")}
                disabled={readonly}
                size="sm"
                placeholder={t("placeholders.terminal")}
                error={
                  !!errors.route?.terminalDropoff?.addressId ||
                  !!errors.route?.terminalDropoff?.companyName
                }
              />
            </FormField>

            {addressRow(
              "route.terminalDropoff",
              dropoffStreet,
              getErrorMessage(errors.route?.terminalDropoff?.address?.street) ??
                getErrorMessage(errors.route?.terminalDropoff?.address?.city),
            )}

            {stopPinRow("route.terminalDropoff")}
          </div>

          {/* Outside the block above, so it survives the terminal being turned
              off. The cut-off belongs to the sailing, not to the gate: a box
              that is staying on site still has a vessel to make, and the batch
              list has a column for the deadline either way. */}
          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label={t("route.cutOffDate")}
              error={getErrorMessage(errors.route?.terminalDropoff?.cutOffDate)}
            >
              <Input
                {...register("route.terminalDropoff.cutOffDate")}
                type="date"
                size="sm"
                readOnly={readonly}
                error={!!errors.route?.terminalDropoff?.cutOffDate}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("route.cutOffTime")}
              // The route-order rule reports here when the cut-off falls before
              // the delivery on the same day.
              error={getErrorMessage(errors.route?.terminalDropoff?.cutOffTime)}
            >
              <Input
                {...register("route.terminalDropoff.cutOffTime")}
                type="time"
                size="sm"
                readOnly={readonly}
                error={!!errors.route?.terminalDropoff?.cutOffTime}
              />
            </FormField>
          </div>
        </div>
      </div>

      {/* ── The stops the three anchors do not cover ────────────────────────
          Set on the order so a batch that all needs the same one is arranged
          once: every container generated from this order inherits the whole
          route, and may then diverge on its own page. */}
      <OrderAdditionalStops
        fields={stopFields}
        remove={removeStop}
        register={register}
        setValue={setValue}
        watch={watch}
        errors={errors}
        readonly={readonly}
        getErrorMessage={getErrorMessage}
        countries={countries}
        apiKey={apiKey}
      />
    </SectionCard>
  );
}
